import { create } from 'zustand'
import { ACCESS_MODES } from './contextAccess'
import { useZustand } from '../hooks/useZustand'
import {
    renameContext as renameContextApi,
    deleteContext,
    updateFavorite,
} from '../hooks/useApi'

// The viewer-vs-owner relationship lives only on currentGraphOwner.accessMode;
// selectors derive everything from it. There is no per-row access flag.
export const useContextsStore = create((set, get) => ({
    contexts: [],
    contextsById: new Map(),
    contextsByName: new Map(),
    currentGraphOwner: null,
    loadedContextList: false,
    accessDenied: null,
    // The tier's open-graph cap the `locked` flags derive from (owner
    // view only; null = unlimited or not enforced).
    openLimit: null,
    setOpenLimit: openLimit => set({ openLimit }),

    // Don't clear the catalog — stale data stays visible until the new fetch
    // lands so slow networks don't blank the drawer mid-navigation.
    startBootstrap: () =>
        set({
            loadedContextList: false,
            accessDenied: null,
        }),

    setContexts: records => {
        const byId = new Map()
        const byName = new Map()
        for (const record of records) {
            byId.set(record.id, record)
            byName.set(record.name, record)
        }
        set({
            contexts: records,
            contextsById: byId,
            contextsByName: byName,
            loadedContextList: true,
        })
    },

    setCurrentGraphOwner: graphOwner => set({ currentGraphOwner: graphOwner }),

    markAccessDenied: accessDenied =>
        set({
            accessDenied,
            loadedContextList: true,
            contexts: [],
            contextsById: new Map(),
            contextsByName: new Map(),
        }),

    renameContext: async (newName, oldName) => {
        // Discard the API return value: the rename endpoint signals success
        // via `res.statusText` which is empty over HTTP/2 and strippable by
        // some proxies.
        await renameContextApi(oldName, newName)
        set(state => {
            const record = state.contextsByName.get(oldName)
            if (!record) return {}
            const next = { ...record, name: newName }
            const contexts = state.contexts.map(existing =>
                existing === record ? next : existing
            )
            const contextsById = new Map(state.contextsById).set(next.id, next)
            const contextsByName = new Map(state.contextsByName)
            contextsByName.delete(oldName)
            contextsByName.set(newName, next)
            return { contexts, contextsById, contextsByName }
        })
        // useZustand.currentContext is a pointer into this catalog;
        // re-point it when the active row was renamed.
        const zustandState = useZustand.getState()
        if (
            zustandState.currentContext === oldName &&
            typeof zustandState.setCurrentContext === 'function'
        ) {
            zustandState.setCurrentContext(newName)
        }
        return newName
    },

    removeContext: async name => {
        await deleteContext(name)
        set(state => {
            const record = state.contextsByName.get(name)
            if (!record) return {}
            const contexts = state.contexts.filter(existing => existing !== record)
            const contextsById = new Map(state.contextsById)
            contextsById.delete(record.id)
            const contextsByName = new Map(state.contextsByName)
            contextsByName.delete(name)
            return { contexts, contextsById, contextsByName }
        })
        const zustandState = useZustand.getState()
        if (
            zustandState.currentContext === name &&
            typeof zustandState.setCurrentContext === 'function'
        ) {
            zustandState.setCurrentContext(null)
        }
    },

    setContextFavorite: async (name, value) => {
        const nextValue = !!value
        await updateFavorite(name, nextValue)
        set(state => {
            const record = state.contextsByName.get(name)
            if (!record) return {}
            const next = { ...record, isFavorite: nextValue }
            const contexts = state.contexts.map(existing =>
                existing === record ? next : existing
            )
            const contextsById = new Map(state.contextsById).set(next.id, next)
            const contextsByName = new Map(state.contextsByName).set(name, next)
            return { contexts, contextsById, contextsByName }
        })
        return nextValue
    },
}))

// ─── Selectors ───────────────────────────────────────────────────────────
//
// Pure functions; pass direcordtly to `useContextsStore(selectName)` for the
// React subscribe path, or call with `useContextsStore.getState()` outside
// React.

/** Contexts the VIEWER owns (returns [] when viewing someone else's graph). For "my contexts" / import targets. */
export const selectOwnedContexts = state =>
    state.currentGraphOwner?.accessMode === ACCESS_MODES.OWNED
        ? state.contexts
        : []

/** Contexts of the currently-displayed graph-owner (regardless of who the viewer is). For drawer / dialogs. */
export const selectOwnerContexts = state => state.contexts

/** All contexts in the store. For id-based lookups where provenance doesn't matter (e.g. label resolution). */
export const selectAllVisibleContexts = state => state.contexts

export const findContextById = (state, id) => state.contextsById.get(id)
export const findContextByName = (state, name) => state.contextsByName.get(name)

/** True when the viewer IS the graph owner — they can edit, share, favorite. */
export const selectIsOwnView = state =>
    state.currentGraphOwner?.accessMode === ACCESS_MODES.OWNED
/**
 * True when the viewer is a non-owner (anonymous OR authenticated cross-user
 * viewer of someone else's public graph) — read-only. Use this to gate any UI
 * affordance that mutates the owner's data or assumes the viewer is the owner
 * (edit/share/rename/delete/favorite controls, /edit-style route segments).
 */
export const selectIsPublicView = state =>
    state.currentGraphOwner?.accessMode === ACCESS_MODES.PUBLIC_OF_OTHER
export const selectGraphOwnerUsername = state =>
    state.currentGraphOwner?.username ?? null
/**
 * True when `contexts` is the VIEWER's own catalog, so its length may be read
 * as "how many graphs do I have": their own graph pages (owner resolved as
 * OWNED) and the service pages, where no graph owner is resolved at all.
 * False on someone else's public graph — the list belongs to that owner — and
 * on an access-denied view, where the list is emptied rather than loaded.
 * Anything deciding something ABOUT THE VIEWER from `contexts` must gate on
 * this; `selectIsOwnView` alone is wrong, as it is false on /apps too.
 */
export const selectContextsAreViewersOwn = state =>
    !state.accessDenied &&
    state.currentGraphOwner?.accessMode !== ACCESS_MODES.PUBLIC_OF_OTHER

// Helpers for the graph-list chip and the lock reason line.
export const selectLockedCount = state =>
    state.contexts.reduce(
        (count, context) => count + (context.locked ? 1 : 0),
        0
    )
export const selectOwnedTotal = state => state.contexts.length
